'use client';

import React, { useState, useMemo } from 'react';
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, Card, Checkbox, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { Venue } from '../../../../shared/models';
import OptimizedImage from '../../components/OptimizedImage';

interface SelectImageFromVenueProps {
    open: boolean;
    venues: Venue[];
    selectedVenue: Venue | null;
    selectedImages: string[];
    onClose: () => void;
    onVenueSelect: (venueId: string) => void;
    onImageSelect: (imageUrl: string) => void;
    onConfirm: () => void;
    postId: string;
}

const SelectImageFromVenue: React.FC<SelectImageFromVenueProps> = ({
    open,
    venues,
    selectedVenue,
    selectedImages,
    onClose,
    onVenueSelect,
    onImageSelect,
    onConfirm,
    postId
}) => {
    // Extract venue images that start with '/venue' and don't contain _400x400 or _800x800
    const venueImages = useMemo(() => {
        if (!selectedVenue?.PhotoUrls) return [];

        // Filter images that start with '/venue' and don't have size suffixes we want to exclude
        const filteredImages = selectedVenue.PhotoUrls
            .filter(url => {
                // Check if the URL contains '/venue' path
                const isVenuePath = url.includes('/venues/');

                // Exclude images with _400x400 or _800x800 in the filename
                const hasExcludedSize = url.includes('_400x400') || url.includes('_800x800');

                return isVenuePath && !hasExcludedSize;
            })
            .map(url => {
                // Extract the original URL and create a unique ID for selection
                return {
                    originalUrl: url,
                    basePath: url
                };
            });

        return filteredImages;
    }, [selectedVenue]);

    const handleConfirm = () => {
        if (selectedImages.length > 0) {
            onConfirm();
        } else {
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: '12px'
                }
            }}
        >
            <DialogTitle sx={{ color: 'white' }}>Select Images from Venue</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3, mt: 1 }}>
                    <FormControl fullWidth>
                        <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>Select Venue</InputLabel>
                        <Select
                            value={selectedVenue?.ID || ''}
                            onChange={(e) => onVenueSelect(e.target.value)}
                            sx={{
                                color: 'white',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.3)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(255,255,255,0.5)'
                                }
                            }}
                        >
                            {venues.map((venue) => (
                                <MenuItem key={venue.ID} value={venue.ID}>
                                    {venue.Name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Grid container spacing={2}>
                    {venueImages.map((imageData, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card
                                sx={{
                                    position: 'relative',
                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: '8px'
                                }}
                            >
                                <OptimizedImage
                                    src={imageData.originalUrl}
                                    width={400}
                                    height={200}
                                    alt={`Venue image ${index + 1}`}
                                    style={{
                                        width: '100%',
                                        height: '200px',
                                        objectFit: 'cover',
                                        opacity: selectedImages.includes(imageData.basePath) ? 0.7 : 1
                                    }}
                                    unoptimized={true}
                                />
                                <Checkbox
                                    checked={selectedImages.includes(imageData.basePath)}
                                    onChange={() => onImageSelect(imageData.basePath)}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: 'white',
                                        '&.Mui-checked': {
                                            color: 'white'
                                        }
                                    }}
                                />
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 3 }}>
                <Button
                    onClick={onClose}
                    sx={{
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.3)',
                        '&:hover': {
                            borderColor: 'white',
                            bgcolor: 'rgba(255,255,255,0.1)'
                        }
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={selectedImages.length === 0}
                    sx={{
                        bgcolor: 'primary.main',
                        '&:hover': {
                            bgcolor: 'primary.dark'
                        }
                    }}
                >
                    Confirm Selection
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SelectImageFromVenue;
